import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import i18next from "i18next";

import { setSupervisorRateLimitError } from "../../../features/user/user-slice";
import axios from "../../../utils/api-client";
import {
	AcknowledgementMetadata,
	AcknowledgementSystemRole,
	AcknowledgementTemplateDetail,
	AcknowledgementTemplateListItem,
	AddQuestionsPayload,
	ChangeEvaluationProgramStatusPayload,
	CreateEvaluationFormPayload,
	CreateEvaluationProgramPayload,
	CreateFormStep1Payload,
	DuplicateEvaluationFormPayload,
	EvaluationFormListItem,
	EvaluationProgramDetail,
	EvaluationProgramListItem,
	LocationOption,
	ProgramJobOption,
	ProgramSkillOption,
	QuestionCategory,
	SaveScoringConfigPayload,
	SkillEvaluationSliceStateTypes,
	UpdateEvaluationFormPayload,
	UpdateEvaluationProgramPayload,
} from "./evaluation-setup-types";


const initialState: SkillEvaluationSliceStateTypes = {
	templates: {
		status: "idle",
		error: "",
		data: [],
	},
	categories: {
		status: "idle",
		error: "",
		data: [] as QuestionCategory[],
	},
	createCategory: {
		status: "idle",
		errorMessage: "",
	},
	updateCategory: {
		status: "idle",
		errorMessage: "",
	},
	deleteCategory: {
		status: "idle",
		errorMessage: "",
	},
	templateDetails: {
		status: "idle",
		error: "",
		data: null,
	},
	editTemplate: {
		status: "idle",
		errorMessage: "",
	},
	skillLevels: {
		status: "idle",
		error: "",
		data: [],
	},

	evaluationFormStatuses: {
		status: "idle",
		errorMessage: "",
		data: [],
	},
	// Form Builder state
	formBuilderDialog: {
		isOpen: false,
		mode: "create",
		formId: null,
	},
	createEvaluationForm: {
		status: "idle",
		errorMessage: "",
	},
	updateEvaluationForm: {
		status: "idle",
		errorMessage: "",
	},
	deleteEvaluationForm: {
		status: "idle",
		errorMessage: "",
	},
	// 2-step form creation progress
	formCreationProgress: {
		step: "idle" as const,
		formId: null,
		errorMessage: "",
	},
	// Auto-save tracking
	currentDraftFormId: null,
	autoSaveStatus: {
		status: "idle" as const,
		lastSaved: null,
		errorMessage: "",
	},
	hasUnsavedChanges: false,
	// Acknowledgements
	acknowledgementTemplates: {
		status: "idle" as const,
		data: [] as AcknowledgementTemplateListItem[],
		error: "",
	},
	acknowledgementMetadata: {
		status: "idle" as const,
		data: null as AcknowledgementMetadata | null,
	},
	evaluationProgramRoles: {
		status: "idle" as const,
		data: [] as AcknowledgementSystemRole[],
	},
	acknowledgementDialog: {
		isOpen: false,
		mode: "create" as const,
		template: null as AcknowledgementTemplateListItem | null,
		detail: null as AcknowledgementTemplateDetail | null,
		detailStatus: "idle" as const,
		saveStatus: "idle" as const,
		saveError: "",
	},
	deleteAcknowledgementTemplate: {
		status: "idle" as const,
		errorMessage: "",
	},
	// Evaluation Programs
	evaluationPrograms: {
		status: "idle" as const,
		data: [] as EvaluationProgramListItem[],
		error: "",
	},
	programDetailDialog: {
		isOpen: false,
		program: null as EvaluationProgramListItem | null,
		detail: null as EvaluationProgramDetail | null,
		detailStatus: "idle" as const,
	},
	programBuilderDialog: {
		isOpen: false,
		mode: "create" as "create" | "edit" | "view",
		programId: null as number | null,
	},
	evaluationProgramMetadata: {
		status: "idle" as const,
		data: null,
	},
	createEvaluationProgram: {
		status: "idle" as const,
		errorMessage: "",
	},
	updateEvaluationProgram: {
		status: "idle" as const,
		errorMessage: "",
	},
	deleteEvaluationProgram: {
		status: "idle" as const,
		errorMessage: "",
	},
	changeEvaluationProgramStatus: {
		status: "idle" as const,
		errorMessage: "",
	},
	programSkills: {
		status: "idle" as const,
		data: [] as ProgramSkillOption[],
	},
	programJobs: {
		status: "idle" as const,
		data: [] as ProgramJobOption[],
	},
	manageLocationsDialog: {
		isOpen: false,
		template: null as EvaluationFormListItem | null,
	},
	scoringDialog: {
		isOpen: false,
		template: null as EvaluationFormListItem | null,
		detail: null,
		detailStatus: "idle" as const,
		saveStatus: "idle" as const,
		saveError: "",
	},
	scoringMetadata: {
		status: "idle" as const,
		data: null,
	},
	fetchLocations: {
		status: "idle" as const,
		data: [] as LocationOption[],
		error: "",
	},
	manageLocations: {
		status: "idle" as const,
		errorMessage: "",
	},
	skillsMatrixFilters: {
		status: "idle" as const,
		data: {
			locations: [] as LocationOption[],
			jobs: [] as ProgramJobOption[],
			skills: [] as ProgramSkillOption[],
		},
	},
};


export const fetchTemplates = createAsyncThunk(
	"skillEvaluation/fetchTemplates",
	async (_, thunkAPI) => {
		const { fulfillWithValue, rejectWithValue, dispatch } = thunkAPI;
		try {
			const res = await axios.get(
				`${process.env.REACT_APP_API_END_POINT}/EvaluationForm/GetAllForCompany`
			);
			return fulfillWithValue(res.data ?? []);
		} catch (error: any) {
			try {
				const errorResponse = error?.response.data;
				errorResponse.status = error?.response?.status;
				if (errorResponse.status === 429) {
					dispatch(setSupervisorRateLimitError());
					return rejectWithValue(errorResponse);
				}
				return rejectWithValue(error.response.data.message);
			} catch (e) {
				return rejectWithValue(i18next.t("evaluation-setup_err-fetching-templates"));
			}
		}
	}
);

export const fetchTemplateDetails = createAsyncThunk(
	"skillEvaluation/fetchTemplateDetails",
	async (templateId: number, thunkAPI) => {
		const { fulfillWithValue, rejectWithValue, dispatch } = thunkAPI;
		try {
			const res = await axios.get(
				`${process.env.REACT_APP_API_END_POINT}/EvaluationForm/GetDetails/${templateId}`
			);
			return fulfillWithValue(res.data);
		} catch (error: any) {
			try {
				const errorResponse = error?.response.data;
				errorResponse.status = error?.response?.status;
				if (errorResponse.status === 429) {
					dispatch(setSupervisorRateLimitError());
					return rejectWithValue(errorResponse);
				}
				return rejectWithValue(error.response.data.message);
			} catch (e) {
				return rejectWithValue(i18next.t("evaluation-setup_err-fetching-template-details"));
			}
		}
	}
);

export const fetchScoringMetadata = createAsyncThunk(
	"skillEvaluation/fetchScoringMetadata",
	async (_, { fulfillWithValue, rejectWithValue, dispatch }) => {
		try {
			const res = await axios.get(
				`${process.env.REACT_APP_API_END_POINT}/EvaluationForm/GetMetadata`
			);
			return fulfillWithValue(res.data);
		} catch (error: any) {
			try {
				const errorResponse = error?.response?.data;
				errorResponse.status = error?.response?.status;
				if (errorResponse.status === 429) {
					dispatch(setSupervisorRateLimitError());
					return rejectWithValue(errorResponse);
				}
				return rejectWithValue(error.response.data.message);
			} catch (e) {
				return rejectWithValue(i18next.t("evaluation-setup_err-fetching-scoring-metadata"));
			}
		}
	}
);

export const fetchScoringConfig = createAsyncThunk(
	"skillEvaluation/fetchScoringConfig",
	async (formId: number, { fulfillWithValue, rejectWithValue, dispatch }) => {
		try {
			const res = await axios.get(
				`${process.env.REACT_APP_API_END_POINT}/EvaluationForm/GetScoringConfig/${formId}`
			);
			return fulfillWithValue(res.data ?? null);
		} catch (error: any) {
			try {
				const errorResponse = error?.response?.data;
				errorResponse.status = error?.response?.status;
				if (errorResponse.status === 429) {
					dispatch(setSupervisorRateLimitError());
					return rejectWithValue(errorResponse);
				}
				return rejectWithValue(error.response.data.message);
			} catch (e) {
				return rejectWithValue(i18next.t("evaluation-setup_err-fetching-scoring-config"));
			}
		}
	}
);

export const saveScoringConfig = createAsyncThunk(
	"skillEvaluation/saveScoringConfig",
	async (
		payload: SaveScoringConfigPayload,
		{ fulfillWithValue, rejectWithValue, dispatch }
	) => {
		try {
			const res = await axios.post(
				`${process.env.REACT_APP_API_END_POINT}/EvaluationForm/SaveScoringConfig`,
				payload
			);
			return fulfillWithValue(res.data);
		} catch (error: any) {
			try {
				const errorResponse = error?.response?.data;
				errorResponse.status = error?.response?.status;
				if (errorResponse.status === 429) {
					dispatch(setSupervisorRateLimitError());
					return rejectWithValue(errorResponse);
				}
				return rejectWithValue(
					error.response?.data?.message || i18next.t("evaluation-setup_err-saving-scoring-config")
				);
			} catch (e) {
				return rejectWithValue(i18next.t("evaluation-setup_err-saving-scoring-config"));
			}
		}
	}
);

interface EditTemplatePayload {
	id: number;
	name: string;
	description: string;
	entityType: string;
	isActive: boolean;
	questions: Array<{
		id?: number;
		questionText: string;
		typeId: number;
		categoryCode: string | null;
		minValue: number | null;
		maxValue: number | null;
		displayOrder: number;
		isRequired: boolean;
		minLength: number | null;
		maxLength: number | null;
		helpText: string | null;
		isActive: boolean;
		options: Array<{
			id?: number;
			optionText: string;
			optionValue: string;
			displayOrder: number;
			isActive: boolean;
		}>;
	}>;
	skillIds: number[];
}

export const editTemplate = createAsyncThunk(
	"skillEvaluation/editTemplate",
	async (
		payload: EditTemplatePayload,
		{ fulfillWithValue, rejectWithValue, dispatch }
	) => {
		try {
			const res = await axios.put(
				`${process.env.REACT_APP_API_END_POINT}/EvaluationForm/UpdateEvaluationForm`,
				payload
			);
			return fulfillWithValue(res.data);
		} catch (error: any) {
			try {
				const errorResponse = error?.response.data;
				errorResponse.status = error?.response?.status;
				if (errorResponse.status === 429) {
					dispatch(setSupervisorRateLimitError());
					return rejectWithValue(errorResponse);
				}
				return rejectWithValue(error.response.data.message);
			} catch (e) {
				return rejectWithValue(i18next.t("evaluation-setup_err-editing-template"));
			}
		}
	}
);

export const fetchSkillLevels = createAsyncThunk(
	"skillEvaluation/fetchSkillLevels",
	async (_, { dispatch, fulfillWithValue, rejectWithValue }) => {
		try {
			const res = await axios.get(
				`${process.env.REACT_APP_API_END_POINT}/Skill/Levels`
			);
			return fulfillWithValue(res.data);
		} catch (error: any) {
			try {
				const errorResponse = error?.response?.data;
				errorResponse.status = error?.response?.status;
				if (errorResponse.status === 429) {
					dispatch(setSupervisorRateLimitError());
					return rejectWithValue(errorResponse);
				}
				return rejectWithValue(error.response.data.message);
			} catch (e) {
				return rejectWithValue(i18next.t("common_error-occurred"));
			}
		}
	}
);




export const fetchEvaluationFormStatuses = createAsyncThunk(
	"skillEvaluation/fetchEvaluationFormStatuses",
	async (_, { dispatch, fulfillWithValue, rejectWithValue }) => {
		try {
			const res = await axios.get(
				`${process.env.REACT_APP_API_END_POINT}/EvaluationForm/GetStatuses`
			);
			return fulfillWithValue(res.data);
		} catch (error: any) {
			try {
				const errorResponse = error?.response?.data;
				errorResponse.status = error?.response?.status;
				if (errorResponse.status === 429) {
					dispatch(setSupervisorRateLimitError());
					return rejectWithValue(errorResponse);
				}
				return rejectWithValue(error.response.data.message);
			} catch (e) {
				return rejectWithValue(i18next.t("evaluation-setup_err-fetching-form-statuses"));
			}
		}
	}
);

// ==========================================
// Evaluation Form CRUD Thunks
// ==========================================

export const createEvaluationForm = createAsyncThunk(
	"skillEvaluation/createEvaluationForm",
	async (
		payload: CreateEvaluationFormPayload,
		{ fulfillWithValue, rejectWithValue, dispatch }
	) => {
		try {
			const res = await axios.post(
				`${process.env.REACT_APP_API_END_POINT}/EvaluationForm/Create`,
				payload
			);
			return fulfillWithValue(res.data);
		} catch (error: any) {
			try {
				const errorResponse = error?.response?.data;
				errorResponse.status = error?.response?.status;
				if (errorResponse.status === 429) {
					dispatch(setSupervisorRateLimitError());
					return rejectWithValue(errorResponse);
				}
				return rejectWithValue(error.response?.data?.message || i18next.t("evaluation-setup_err-creating-evaluation-form"));
			} catch (e) {
				return rejectWithValue(i18next.t("evaluation-setup_err-creating-evaluation-form"));
			}
		}
	}
);

export const updateEvaluationForm = createAsyncThunk(
	"skillEvaluation/updateEvaluationForm",
	async (
		payload: UpdateEvaluationFormPayload,
		{ fulfillWithValue, rejectWithValue, dispatch }
	) => {
		try {
			const res = await axios.put(
				`${process.env.REACT_APP_API_END_POINT}/EvaluationForm/Update`,
				payload
			);
			return fulfillWithValue(res.data);
		} catch (error: any) {
			try {
				const errorResponse = error?.response?.data;
				errorResponse.status = error?.response?.status;
				if (errorResponse.status === 429) {
					dispatch(setSupervisorRateLimitError());
					return rejectWithValue(errorResponse);
				}
				return rejectWithValue(error.response?.data?.message || i18next.t("evaluation-setup_err-updating-evaluation-form"));
			} catch (e) {
				return rejectWithValue(i18next.t("evaluation-setup_err-updating-evaluation-form"));
			}
		}
	}
);

export const deleteEvaluationForm = createAsyncThunk(
	"skillEvaluation/deleteEvaluationForm",
	async (formId: number, { fulfillWithValue, rejectWithValue, dispatch }) => {
		try {
			const res = await axios.delete(
				`${process.env.REACT_APP_API_END_POINT}/EvaluationForm/Delete/${formId}`
			);
			return fulfillWithValue({ formId, message: res.data?.message });
		} catch (error: any) {
			try {
				const errorResponse = error?.response?.data;
				errorResponse.status = error?.response?.status;
				if (errorResponse.status === 429) {
					dispatch(setSupervisorRateLimitError());
					return rejectWithValue(errorResponse);
				}
				return rejectWithValue(error.response?.data?.message || i18next.t("evaluation-setup_err-deleting-evaluation-form"));
			} catch (e) {
				return rejectWithValue(i18next.t("evaluation-setup_err-deleting-evaluation-form"));
			}
		}
	}
);

// Copy is always a new Draft; the server names it "<source> (Copy N)"
export const duplicateEvaluationForm = createAsyncThunk(
	"skillEvaluation/duplicateEvaluationForm",
	async (
		{ formId, includeLocation, includeScoring }: DuplicateEvaluationFormPayload,
		{ fulfillWithValue, rejectWithValue, dispatch }
	) => {
		try {
			const res = await axios.post(
				`${process.env.REACT_APP_API_END_POINT}/EvaluationForm/Duplicate/${formId}`,
				{ includeLocation, includeScoring }
			);
			return fulfillWithValue(res.data);
		} catch (error: any) {
			try {
				const errorResponse = error?.response?.data;
				errorResponse.status = error?.response?.status;
				if (errorResponse.status === 429) {
					dispatch(setSupervisorRateLimitError());
					return rejectWithValue(errorResponse);
				}
				return rejectWithValue(error.response?.data?.message || i18next.t("evaluation-setup_err-duplicating-evaluation-form"));
			} catch (e) {
				return rejectWithValue(i18next.t("evaluation-setup_err-duplicating-evaluation-form"));
			}
		}
	}
);

// Step 1: Create form with basic info only
export const createEvaluationFormStep1 = createAsyncThunk(
	"skillEvaluation/createEvaluationFormStep1",
	async (
		payload: CreateFormStep1Payload,
		{ fulfillWithValue, rejectWithValue, dispatch }
	) => {
		try {
			const res = await axios.post(
				`${process.env.REACT_APP_API_END_POINT}/EvaluationForm/Create`,
				payload
			);
			return fulfillWithValue(res.data);
		} catch (error: any) {
			try {
				const errorResponse = error?.response?.data;
				errorResponse.status = error?.response?.status;
				if (errorResponse.status === 429) {
					dispatch(setSupervisorRateLimitError());
					return rejectWithValue(errorResponse);
				}
				return rejectWithValue(error.response?.data?.message || i18next.t("evaluation-setup_err-creating-form"));
			} catch (e) {
				return rejectWithValue(i18next.t("evaluation-setup_err-creating-form"));
			}
		}
	}
);

// Step 2: Add questions to the form
export const addQuestionsToForm = createAsyncThunk(
	"skillEvaluation/addQuestionsToForm",
	async (
		payload: AddQuestionsPayload,
		{ fulfillWithValue, rejectWithValue, dispatch }
	) => {
		try {
			const res = await axios.post(
				`${process.env.REACT_APP_API_END_POINT}/EvaluationForm/AddQuestions`,
				payload
			);
			return fulfillWithValue(res.data);
		} catch (error: any) {
			try {
				const errorResponse = error?.response?.data;
				errorResponse.status = error?.response?.status;
				if (errorResponse.status === 429) {
					dispatch(setSupervisorRateLimitError());
					return rejectWithValue(errorResponse);
				}
				return rejectWithValue(error.response?.data?.message || i18next.t("evaluation-setup_err-adding-questions"));
			} catch (e) {
				return rejectWithValue(i18next.t("evaluation-setup_err-adding-questions"));
			}
		}
	}
);

// ==========================================
// New Auto-save Thunks for Form Builder
// ==========================================

// Create form draft (header only) - called after Step 1
export const createFormDraft = createAsyncThunk(
	"skillEvaluation/createFormDraft",
	async (
		payload: import("./evaluation-setup-types").CreateFormDraftPayload,
		{ fulfillWithValue, rejectWithValue, dispatch }
	) => {
		try {
			const res = await axios.post(
				`${process.env.REACT_APP_API_END_POINT}/EvaluationForm/Create`,
				payload
			);
			return fulfillWithValue(res.data);
		} catch (error: any) {
			try {
				const errorResponse = error?.response?.data;
				errorResponse.status = error?.response?.status;
				if (errorResponse.status === 429) {
					dispatch(setSupervisorRateLimitError());
					return rejectWithValue(errorResponse);
				}
				return rejectWithValue(error.response?.data?.message || i18next.t("evaluation-setup_err-creating-form-draft"));
			} catch (e) {
				return rejectWithValue(i18next.t("evaluation-setup_err-creating-form-draft"));
			}
		}
	}
);

// Auto-save questions - called on question add/update/delete
export const autoSaveQuestions = createAsyncThunk(
	"skillEvaluation/autoSaveQuestions",
	async (
		payload: import("./evaluation-setup-types").ManageQuestionsPayload,
		{ fulfillWithValue, rejectWithValue, dispatch }
	) => {
		try {
			const body = payload.categoryOrder
				? { questions: payload.questions, categoryOrder: payload.categoryOrder }
				: { questions: payload.questions };
			const res = await axios.put(
				`${process.env.REACT_APP_API_END_POINT}/EvaluationForm/ManageQuestions/${payload.formId}`,
				body
			);
			return fulfillWithValue(res.data);
		} catch (error: any) {
			try {
				const errorResponse = error?.response?.data;
				errorResponse.status = error?.response?.status;
				if (errorResponse.status === 429) {
					dispatch(setSupervisorRateLimitError());
					return rejectWithValue(errorResponse);
				}
				// Status goes in meta so callers can react to a 400 without
				// changing the string payload the reducer stores
				return rejectWithValue(
					error.response?.data?.message || i18next.t("evaluation-setup_err-saving-questions"),
					{ status: errorResponse.status }
				);
			} catch (e) {
				return rejectWithValue(i18next.t("evaluation-setup_err-saving-questions"));
			}
		}
	}
);

// Update form header (name/description only) - called when header changes
export const updateFormHeader = createAsyncThunk(
	"skillEvaluation/updateFormHeader",
	async (
		payload: import("./evaluation-setup-types").UpdateFormHeaderPayload,
		{ fulfillWithValue, rejectWithValue, dispatch }
	) => {
		try {
			const res = await axios.put(
				`${process.env.REACT_APP_API_END_POINT}/EvaluationForm/Update`,
				payload
			);
			return fulfillWithValue(res.data);
		} catch (error: any) {
			try {
				const errorResponse = error?.response?.data;
				errorResponse.status = error?.response?.status;
				if (errorResponse.status === 429) {
					dispatch(setSupervisorRateLimitError());
					return rejectWithValue(errorResponse);
				}
				return rejectWithValue(error.response?.data?.message || i18next.t("evaluation-setup_err-updating-form-header"));
			} catch (e) {
				return rejectWithValue(i18next.t("evaluation-setup_err-updating-form-header"));
			}
		}
	}
);

// Change form status (Draft/Published) - called on final save
export const changeFormStatus = createAsyncThunk(
	"skillEvaluation/changeFormStatus",
	async (
		payload: import("./evaluation-setup-types").ChangeStatusPayload,
		{ fulfillWithValue, rejectWithValue, dispatch }
	) => {
		try {
			const res = await axios.put(
				`${process.env.REACT_APP_API_END_POINT}/EvaluationForm/ChangeStatus`,
				payload
			);
			return fulfillWithValue(res.data);
		} catch (error: any) {
			try {
				const errorResponse = error?.response?.data;
				errorResponse.status = error?.response?.status;
				if (errorResponse.status === 429) {
					dispatch(setSupervisorRateLimitError());
					return rejectWithValue(errorResponse);
				}
				return rejectWithValue(error.response?.data?.message || i18next.t("evaluation-setup_err-changing-form-status"));
			} catch (e) {
				return rejectWithValue(i18next.t("evaluation-setup_err-changing-form-status"));
			}
		}
	}
);

// ==========================================
// Acknowledgement Workflow Thunks
// ==========================================

export const fetchAcknowledgementMetadata = createAsyncThunk(
	"skillEvaluation/fetchAcknowledgementMetadata",
	async (_, { fulfillWithValue, rejectWithValue }) => {
		try {
			const res = await axios.get(
				`${process.env.REACT_APP_API_END_POINT}/AcknowledgementWorkflow/GetMetadata`
			);
			return fulfillWithValue(res.data);
		} catch (error: any) {
			return rejectWithValue(
				error?.response?.data?.message ?? i18next.t("evaluation-setup_err-fetching-ack-metadata")
			);
		}
	}
);

export const fetchEvaluationProgramRoles = createAsyncThunk(
	"skillEvaluation/fetchEvaluationProgramRoles",
	async (_, { fulfillWithValue, rejectWithValue }) => {
		try {
			const res = await axios.get(
				`${process.env.REACT_APP_API_END_POINT}/WorkerEvaluationInstance/GetRoles`
			);
			const raw: Array<{ roleId: number; roleName: string }> = res.data ?? [];
			const mapped: AcknowledgementSystemRole[] = raw.map((r) => ({
				id: r.roleId,
				name: r.roleName,
			}));
			return fulfillWithValue(mapped);
		} catch (error: any) {
			return rejectWithValue(
				error?.response?.data?.message ?? i18next.t("evaluation-setup_err-fetching-eval-program-roles")
			);
		}
	}
);

export const fetchAcknowledgementTemplates = createAsyncThunk(
	"skillEvaluation/fetchAcknowledgementTemplates",
	async (_, { fulfillWithValue, rejectWithValue }) => {
		try {
			const res = await axios.get(
				`${process.env.REACT_APP_API_END_POINT}/AcknowledgementWorkflow/GetAllForCompany`
			);
			if (res.status === 204) return fulfillWithValue([]);
			return fulfillWithValue(res.data ?? []);
		} catch (error: any) {
			return rejectWithValue(
				error?.response?.data?.message ?? i18next.t("evaluation-setup_err-fetching-ack-templates")
			);
		}
	}
);

export const fetchAcknowledgementTemplateDetail = createAsyncThunk(
	"skillEvaluation/fetchAcknowledgementTemplateDetail",
	async (id: number, { fulfillWithValue, rejectWithValue }) => {
		try {
			const res = await axios.get(
				`${process.env.REACT_APP_API_END_POINT}/AcknowledgementWorkflow/GetDetails/${id}`
			);
			return fulfillWithValue(res.data);
		} catch (error: any) {
			return rejectWithValue(
				error?.response?.data?.message ?? i18next.t("evaluation-setup_err-fetching-template-details")
			);
		}
	}
);

interface AckTemplateStepPayload {
	stepOrder: number;
	assigneeTypeCode: string;
	assigneeRoleId: number | null;
	stepActionCode: string;
	requiresSignature: boolean;
}

interface CreateAcknowledgementTemplatePayload {
	name: string;
	description: string;
	processingModeCode: string;
	steps: AckTemplateStepPayload[];
}

export const createAcknowledgementTemplate = createAsyncThunk(
	"skillEvaluation/createAcknowledgementTemplate",
	async (
		payload: CreateAcknowledgementTemplatePayload,
		{ fulfillWithValue, rejectWithValue, dispatch }
	) => {
		try {
			const res = await axios.post(
				`${process.env.REACT_APP_API_END_POINT}/AcknowledgementWorkflow/Create`,
				payload
			);
			dispatch(fetchAcknowledgementTemplates());
			return fulfillWithValue(res.data);
		} catch (error: any) {
			return rejectWithValue(
				error?.response?.data?.message ?? i18next.t("evaluation-setup_err-creating-ack-template")
			);
		}
	}
);

interface UpdateAcknowledgementTemplatePayload extends CreateAcknowledgementTemplatePayload {
	id: number;
}

export const updateAcknowledgementTemplate = createAsyncThunk(
	"skillEvaluation/updateAcknowledgementTemplate",
	async (
		payload: UpdateAcknowledgementTemplatePayload,
		{ fulfillWithValue, rejectWithValue, dispatch }
	) => {
		try {
			const res = await axios.put(
				`${process.env.REACT_APP_API_END_POINT}/AcknowledgementWorkflow/Update`,
				payload
			);
			dispatch(fetchAcknowledgementTemplates());
			return fulfillWithValue(res.data);
		} catch (error: any) {
			return rejectWithValue(
				error?.response?.data?.message ?? i18next.t("evaluation-setup_err-updating-ack-template")
			);
		}
	}
);

export const deleteAcknowledgementTemplateThunk = createAsyncThunk(
	"skillEvaluation/deleteAcknowledgementTemplate",
	async (id: number, { fulfillWithValue, rejectWithValue, dispatch }) => {
		try {
			await axios.delete(
				`${process.env.REACT_APP_API_END_POINT}/AcknowledgementWorkflow/Delete/${id}`
			);
			dispatch(fetchAcknowledgementTemplates());
			return fulfillWithValue(id);
		} catch (error: any) {
			return rejectWithValue(
				error?.response?.data?.message ?? i18next.t("evaluation-setup_err-deleting-ack-template")
			);
		}
	}
);

// ==========================================
// Manage Locations Thunks
// ==========================================

export const fetchEvaluationFormLocations = createAsyncThunk(
	"skillEvaluation/fetchEvaluationFormLocations",
	async (companyId: number, { fulfillWithValue, rejectWithValue }) => {
		try {
			const res = await axios.get(
				`${process.env.REACT_APP_API_END_POINT}/companies/${companyId}/locations/`
			);
			return fulfillWithValue(res.data ?? []);
		} catch (error: any) {
			return rejectWithValue(
				error?.response?.data?.message ?? i18next.t("evaluation-setup_err-fetching-locations")
			);
		}
	}
);

interface ManageLocationsPayload {
	evaluationFormId: number;
	locationIds: number[];
}

export const manageEvaluationFormLocations = createAsyncThunk(
	"skillEvaluation/manageEvaluationFormLocations",
	async (payload: ManageLocationsPayload, { fulfillWithValue, rejectWithValue }) => {
		try {
			const res = await axios.post(
				`${process.env.REACT_APP_API_END_POINT}/EvaluationForm/ManageLocations`,
				payload
			);
			return fulfillWithValue(res.data);
		} catch (error: any) {
			return rejectWithValue(
				error?.response?.data?.message ?? i18next.t("evaluation-setup_err-managing-locations")
			);
		}
	}
);

// ==========================================
// Evaluation Program Thunks
// ==========================================

export const fetchEvaluationPrograms = createAsyncThunk(
	"skillEvaluation/fetchEvaluationPrograms",
	async (_, { fulfillWithValue, rejectWithValue }) => {
		try {
			const res = await axios.get(
				`${process.env.REACT_APP_API_END_POINT}/EvaluationProgram/GetAllForCompany`
			);
			if (res.status === 204) return fulfillWithValue([]);
			return fulfillWithValue(res.data ?? []);
		} catch (error: any) {
			return rejectWithValue(
				error?.response?.data?.message ?? i18next.t("evaluation-setup_err-fetching-eval-programs")
			);
		}
	}
);

export const fetchEvaluationProgramDetail = createAsyncThunk(
	"skillEvaluation/fetchEvaluationProgramDetail",
	async (id: number, { fulfillWithValue, rejectWithValue }) => {
		try {
			const res = await axios.get(
				`${process.env.REACT_APP_API_END_POINT}/EvaluationProgram/GetDetails/${id}`
			);
			return fulfillWithValue(res.data);
		} catch (error: any) {
			return rejectWithValue(
				error?.response?.data?.message ?? i18next.t("evaluation-setup_err-fetching-program-details")
			);
		}
	}
);

export const fetchEvaluationProgramMetadata = createAsyncThunk(
	"skillEvaluation/fetchEvaluationProgramMetadata",
	async (_, { fulfillWithValue, rejectWithValue }) => {
		try {
			const res = await axios.get(
				`${process.env.REACT_APP_API_END_POINT}/EvaluationProgram/GetMetadata`
			);
			return fulfillWithValue(res.data);
		} catch (error: any) {
			return rejectWithValue(
				error?.response?.data?.message ?? i18next.t("evaluation-setup_err-fetching-program-metadata")
			);
		}
	}
);

export const createEvaluationProgram = createAsyncThunk(
	"skillEvaluation/createEvaluationProgram",
	async (
		payload: CreateEvaluationProgramPayload,
		{ fulfillWithValue, rejectWithValue }
	) => {
		try {
			const res = await axios.post(
				`${process.env.REACT_APP_API_END_POINT}/EvaluationProgram/Create`,
				payload
			);
			return fulfillWithValue(res.data);
		} catch (error: any) {
			return rejectWithValue(
				error?.response?.data?.message ?? i18next.t("evaluation-setup_err-creating-eval-program")
			);
		}
	}
);

export const updateEvaluationProgram = createAsyncThunk(
	"skillEvaluation/updateEvaluationProgram",
	async (
		payload: UpdateEvaluationProgramPayload,
		{ fulfillWithValue, rejectWithValue }
	) => {
		try {
			const res = await axios.put(
				`${process.env.REACT_APP_API_END_POINT}/EvaluationProgram/Update`,
				payload
			);
			return fulfillWithValue(res.data);
		} catch (error: any) {
			return rejectWithValue(
				error?.response?.data?.message ?? i18next.t("evaluation-setup_err-updating-eval-program")
			);
		}
	}
);

export const deleteEvaluationProgramThunk = createAsyncThunk(
	"skillEvaluation/deleteEvaluationProgram",
	async (id: number, { fulfillWithValue, rejectWithValue }) => {
		try {
			await axios.delete(
				`${process.env.REACT_APP_API_END_POINT}/EvaluationProgram/Delete/${id}`
			);
			return fulfillWithValue(id);
		} catch (error: any) {
			return rejectWithValue(
				error?.response?.data?.message ?? i18next.t("evaluation-setup_err-deleting-eval-program")
			);
		}
	}
);

export const changeEvaluationProgramStatusThunk = createAsyncThunk(
	"skillEvaluation/changeEvaluationProgramStatus",
	async (
		payload: ChangeEvaluationProgramStatusPayload,
		{ fulfillWithValue, rejectWithValue }
	) => {
		try {
			const res = await axios.put(
				`${process.env.REACT_APP_API_END_POINT}/EvaluationProgram/ChangeStatus`,
				payload
			);
			return fulfillWithValue(res.data);
		} catch (error: any) {
			return rejectWithValue(
				error?.response?.data?.message ?? i18next.t("evaluation-setup_err-changing-program-status")
			);
		}
	}
);

export const fetchProgramSkills = createAsyncThunk(
	"skillEvaluation/fetchProgramSkills",
	async (_, { fulfillWithValue, rejectWithValue }) => {
		try {
			const res = await axios.get(
				`${process.env.REACT_APP_API_END_POINT}/Skill/GetSkillsByUser`
			);
			return fulfillWithValue(res.data ?? []);
		} catch (error: any) {
			return rejectWithValue(
				error?.response?.data?.message ?? i18next.t("evaluation-setup_err-fetching-skills")
			);
		}
	}
);

export const fetchProgramJobs = createAsyncThunk(
	"skillEvaluation/fetchProgramJobs",
	async (_, { fulfillWithValue, rejectWithValue }) => {
		try {
			const res = await axios.get(
				`${process.env.REACT_APP_API_END_POINT}/Jobs/GetJobsByUser`
			);
			return fulfillWithValue(res.data ?? []);
		} catch (error: any) {
			return rejectWithValue(
				error?.response?.data?.message ?? i18next.t("evaluation-setup_err-fetching-jobs")
			);
		}
	}
);

export const fetchSkillsMatrixFilters = createAsyncThunk(
	"skillEvaluation/fetchSkillsMatrixFilters",
	async (_, { fulfillWithValue, rejectWithValue }) => {
		try {
			const res = await axios.get(
				`${process.env.REACT_APP_API_END_POINT}/SkillsMatrix/GetFiltersV2?includeFilterRelationships=true`
			);
			return fulfillWithValue(res.data ?? {});
		} catch (error: any) {
			return rejectWithValue(
				error?.response?.data?.message ?? i18next.t("evaluation-setup_err-fetching-skills-matrix")
			);
		}
	}
);

// ==========================================
// Question Category Thunks
// ==========================================

export const fetchCategories = createAsyncThunk(
	"skillEvaluation/fetchCategories",
	async (_, { fulfillWithValue, rejectWithValue }) => {
		try {
			const res = await axios.get(
				`${process.env.REACT_APP_API_END_POINT}/EvaluationQuestionCategory/GetAll`
			);
			if (res.status === 204) return fulfillWithValue([]);
			return fulfillWithValue(res.data ?? []);
		} catch (error: any) {
			return rejectWithValue(
				error?.response?.data?.message ?? i18next.t("evaluation-setup_err-fetching-question-categories")
			);
		}
	}
);

interface CategoryCreatePayload {
	name: string;
	description?: string;
}

export const createQuestionCategory = createAsyncThunk(
	"skillEvaluation/createQuestionCategory",
	async (payload: CategoryCreatePayload, { fulfillWithValue, rejectWithValue, dispatch }) => {
		try {
			const res = await axios.post(
				`${process.env.REACT_APP_API_END_POINT}/EvaluationQuestionCategory/Create`,
				payload
			);
			dispatch(fetchCategories());
			return fulfillWithValue(res.data);
		} catch (error: any) {
			return rejectWithValue(
				error?.response?.data?.message ?? i18next.t("evaluation-setup_err-creating-question-category")
			);
		}
	}
);

interface CategoryUpdatePayload {
	id: number;
	name: string;
	description?: string;
}

export const updateQuestionCategory = createAsyncThunk(
	"skillEvaluation/updateQuestionCategory",
	async (payload: CategoryUpdatePayload, { fulfillWithValue, rejectWithValue, dispatch }) => {
		try {
			const res = await axios.put(
				`${process.env.REACT_APP_API_END_POINT}/EvaluationQuestionCategory/Update`,
				payload
			);
			dispatch(fetchCategories());
			return fulfillWithValue(res.data);
		} catch (error: any) {
			return rejectWithValue(
				error?.response?.data?.message ?? i18next.t("evaluation-setup_err-updating-question-category")
			);
		}
	}
);

export const deleteQuestionCategory = createAsyncThunk(
	"skillEvaluation/deleteQuestionCategory",
	async (id: number, { fulfillWithValue, rejectWithValue, dispatch }) => {
		try {
			const res = await axios.delete(
				`${process.env.REACT_APP_API_END_POINT}/EvaluationQuestionCategory/Delete/${id}`
			);
			dispatch(fetchCategories());
			return fulfillWithValue(res.data);
		} catch (error: any) {
			return rejectWithValue(
				error?.response?.data?.message ?? i18next.t("evaluation-setup_err-deleting-question-category")
			);
		}
	}
);

export const SkillEvaluation = createSlice({
	name: "skillEvaluation",
	initialState,
	reducers: {
		resetEditTemplate: (state) => {
			state.editTemplate.status = "idle";
			state.editTemplate.errorMessage = "";
		},
resetTemplateDetails: (state) => {
			state.templateDetails.status = "idle";
			state.templateDetails.error = "";
			state.templateDetails.data = null;
		},
		// Form Builder Dialog reducers
		openFormBuilderDialog: (state, action) => {
			state.formBuilderDialog.isOpen = true;
			state.formBuilderDialog.mode = action.payload?.mode || "create";
			state.formBuilderDialog.formId = action.payload?.formId ?? null;
			state.formBuilderDialog.initialStep = action.payload?.initialStep;
			// Reset form operation states
			state.createEvaluationForm = { status: "idle", errorMessage: "" };
			state.updateEvaluationForm = { status: "idle", errorMessage: "" };
			// Reset template details if opening for create
			if (action.payload?.mode === "create") {
				state.templateDetails = { status: "idle", error: "", data: null };
			}
		},
		closeFormBuilderDialog: (state) => {
			state.formBuilderDialog.isOpen = false;
			state.formBuilderDialog.mode = "create";
			state.formBuilderDialog.formId = null;
			state.formBuilderDialog.initialStep = undefined;
			// Reset form operation states
			state.createEvaluationForm = { status: "idle", errorMessage: "" };
			state.updateEvaluationForm = { status: "idle", errorMessage: "" };
			// Reset auto-save state
			state.currentDraftFormId = null;
			state.autoSaveStatus = { status: "idle", lastSaved: null, errorMessage: "" };
			state.hasUnsavedChanges = false;
			// Reset template details
			state.templateDetails = { status: "idle", error: "", data: null };
		},
		resetCreateEvaluationForm: (state) => {
			state.createEvaluationForm = { status: "idle", errorMessage: "" };
		},
		resetUpdateEvaluationForm: (state) => {
			state.updateEvaluationForm = { status: "idle", errorMessage: "" };
		},
		resetDeleteEvaluationForm: (state) => {
			state.deleteEvaluationForm = { status: "idle", errorMessage: "" };
		},
		resetFormCreationProgress: (state) => {
			state.formCreationProgress = {
				step: "idle",
				formId: null,
				errorMessage: "",
			};
		},
		// Auto-save reducers
		setCurrentDraftFormId: (state, action) => {
			state.currentDraftFormId = action.payload;
		},
		setHasUnsavedChanges: (state, action) => {
			state.hasUnsavedChanges = action.payload;
		},
		resetAutoSaveStatus: (state) => {
			state.autoSaveStatus = {
				status: "idle",
				lastSaved: null,
				errorMessage: "",
			};
			state.hasUnsavedChanges = false;
		},
		clearAutoSaveResults: (state) => {
			if (state.autoSaveStatus.lastSaveResults) {
				state.autoSaveStatus.lastSaveResults = null;
			}
		},
		// Acknowledgement template reducers
		openCreateAcknowledgementDialog: (state) => {
			state.acknowledgementDialog.isOpen = true;
			state.acknowledgementDialog.mode = "create";
			state.acknowledgementDialog.template = null;
			state.acknowledgementDialog.detail = null;
			state.acknowledgementDialog.detailStatus = "idle";
			state.acknowledgementDialog.saveStatus = "idle";
			state.acknowledgementDialog.saveError = "";
		},
		openEditAcknowledgementDialog: (state, action) => {
			state.acknowledgementDialog.isOpen = true;
			state.acknowledgementDialog.mode = "edit";
			state.acknowledgementDialog.template = action.payload;
			state.acknowledgementDialog.detail = null;
			state.acknowledgementDialog.detailStatus = "idle";
			state.acknowledgementDialog.saveStatus = "idle";
			state.acknowledgementDialog.saveError = "";
		},
		// Read-only (W1.3): users who can view acknowledgements but not edit them.
		openViewAcknowledgementDialog: (state, action) => {
			state.acknowledgementDialog.isOpen = true;
			state.acknowledgementDialog.mode = "view";
			state.acknowledgementDialog.template = action.payload;
			state.acknowledgementDialog.detail = null;
			state.acknowledgementDialog.detailStatus = "idle";
			state.acknowledgementDialog.saveStatus = "idle";
			state.acknowledgementDialog.saveError = "";
		},
		closeAcknowledgementDialog: (state) => {
			state.acknowledgementDialog.isOpen = false;
			state.acknowledgementDialog.template = null;
			state.acknowledgementDialog.detail = null;
			state.acknowledgementDialog.detailStatus = "idle";
			state.acknowledgementDialog.saveStatus = "idle";
			state.acknowledgementDialog.saveError = "";
		},
		resetDeleteAcknowledgementTemplate: (state) => {
			state.deleteAcknowledgementTemplate.status = "idle";
			state.deleteAcknowledgementTemplate.errorMessage = "";
		},
		// Manage Locations Dialog reducers
		openManageLocationsDialog: (state, action) => {
			state.manageLocationsDialog.isOpen = true;
			state.manageLocationsDialog.template = action.payload;
			state.fetchLocations = { status: "idle", data: [], error: "" };
			state.manageLocations = { status: "idle", errorMessage: "" };
			state.formAssignedLocations = { status: "idle", data: [], error: "" };
		},
		closeManageLocationsDialog: (state) => {
			state.manageLocationsDialog.isOpen = false;
			state.manageLocationsDialog.template = null;
			state.manageLocations = { status: "idle", errorMessage: "" };
		},
		openScoringDialog: (state, action) => {
			state.scoringDialog.isOpen = true;
			state.scoringDialog.template = action.payload;
			state.scoringDialog.detail = null;
			state.scoringDialog.detailStatus = "idle";
			state.scoringDialog.saveStatus = "idle";
			state.scoringDialog.saveError = "";
		},
		closeScoringDialog: (state) => {
			state.scoringDialog.isOpen = false;
			state.scoringDialog.template = null;
			state.scoringDialog.detail = null;
			state.scoringDialog.detailStatus = "idle";
			state.scoringDialog.saveStatus = "idle";
			state.scoringDialog.saveError = "";
		},
		resetSaveScoringStatus: (state) => {
			state.scoringDialog.saveStatus = "idle";
			state.scoringDialog.saveError = "";
		},
		// Evaluation Program reducers
		openProgramDetailDialog: (state, action) => {
			state.programDetailDialog.isOpen = true;
			state.programDetailDialog.program = action.payload;
			state.programDetailDialog.detail = null;
			state.programDetailDialog.detailStatus = "idle";
		},
		closeProgramDetailDialog: (state) => {
			state.programDetailDialog.isOpen = false;
			state.programDetailDialog.program = null;
			state.programDetailDialog.detail = null;
			state.programDetailDialog.detailStatus = "idle";
		},
		openProgramBuilderDialog: (state, action) => {
			state.programBuilderDialog.isOpen = true;
			state.programBuilderDialog.mode = action.payload?.mode ?? "create";
			state.programBuilderDialog.programId = action.payload?.programId ?? null;
			state.createEvaluationProgram = { status: "idle", errorMessage: "" };
			state.updateEvaluationProgram = { status: "idle", errorMessage: "" };
		},
		closeProgramBuilderDialog: (state) => {
			state.programBuilderDialog.isOpen = false;
			state.programBuilderDialog.mode = "create";
			state.programBuilderDialog.programId = null;
			state.createEvaluationProgram = { status: "idle", errorMessage: "" };
			state.updateEvaluationProgram = { status: "idle", errorMessage: "" };
			state.changeEvaluationProgramStatus = { status: "idle", errorMessage: "" };
		},
		resetChangeEvaluationProgramStatus: (state) => {
			state.changeEvaluationProgramStatus = { status: "idle", errorMessage: "" };
		},
		resetDeleteEvaluationProgram: (state) => {
			state.deleteEvaluationProgram = { status: "idle", errorMessage: "" };
		},
		// Question Category reducers
		resetCreateCategory: (state) => {
			state.createCategory = { status: "idle", errorMessage: "" };
		},
		resetUpdateCategory: (state) => {
			state.updateCategory = { status: "idle", errorMessage: "" };
		},
		resetDeleteCategory: (state) => {
			state.deleteCategory = { status: "idle", errorMessage: "" };
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchTemplates.pending, (state) => {
				state.templates.status = "pending";
			})
			.addCase(fetchTemplates.fulfilled, (state, action) => {
				state.templates.status = "fulfilled";
				state.templates.data = action.payload;
			})
			.addCase(fetchTemplates.rejected, (state, action: any) => {
				if (action.payload?.status === 429) {
					state.templates.status = "error";
				} else {
					state.templates.status = "rejected";
					state.templates.error = action.error.message ?? "";
				}
			})
			.addCase(fetchTemplateDetails.pending, (state) => {
				state.templateDetails.status = "pending";
			})
			.addCase(fetchTemplateDetails.fulfilled, (state, action) => {
				state.templateDetails.status = "fulfilled";
				state.templateDetails.data = action.payload;
			})
			.addCase(fetchTemplateDetails.rejected, (state, action: any) => {
				if (action.payload?.status === 429) {
					state.templateDetails.status = "error";
				} else {
					state.templateDetails.status = "rejected";
					state.templateDetails.error = action.error.message ?? "";
				}
			})
			.addCase(editTemplate.pending, (state) => {
				state.editTemplate.status = "pending";
			})
			.addCase(editTemplate.fulfilled, (state) => {
				state.editTemplate.status = "fulfilled";
			})
			.addCase(editTemplate.rejected, (state, action: any) => {
				if (action.payload?.status === 429) {
					state.editTemplate.status = "error";
				} else {
					state.editTemplate.status = "rejected";
					state.editTemplate.errorMessage =
						action.payload ?? action.error.message ?? i18next.t("common_error-occurred");
				}
			})
			.addCase(fetchSkillLevels.pending, (state) => {
				state.skillLevels.status = "pending";
				state.skillLevels.error = "";
			})
			.addCase(fetchSkillLevels.fulfilled, (state, action) => {
				state.skillLevels.status = "fulfilled";
				state.skillLevels.data = action.payload;
				state.skillLevels.error = "";
			})
			.addCase(fetchSkillLevels.rejected, (state, action: any) => {
				if (action.payload?.status === 429) {
					state.skillLevels.status = "error";
				} else {
					state.skillLevels.status = "rejected";
					state.skillLevels.error = action.error.message ?? i18next.t("common_error-occurred");
				}
			})
			// Fetch Evaluation Form Statuses
			.addCase(fetchEvaluationFormStatuses.pending, (state) => {
				state.evaluationFormStatuses.status = "pending";
			})
			.addCase(fetchEvaluationFormStatuses.fulfilled, (state, action) => {
				state.evaluationFormStatuses.status = "fulfilled";
				state.evaluationFormStatuses.data = action.payload;
				state.evaluationFormStatuses.errorMessage = "";
			})
			.addCase(fetchEvaluationFormStatuses.rejected, (state, action: any) => {
				if (action.payload?.status === 429) {
					state.evaluationFormStatuses.status = "error";
				} else {
					state.evaluationFormStatuses.status = "error";
					state.evaluationFormStatuses.errorMessage = action.payload ?? action.error.message ?? i18next.t("common_error-occurred");
				}
			})
			// Create Evaluation Form
			.addCase(createEvaluationForm.pending, (state) => {
				state.createEvaluationForm.status = "pending";
				state.createEvaluationForm.errorMessage = "";
			})
			.addCase(createEvaluationForm.fulfilled, (state) => {
				state.createEvaluationForm.status = "fulfilled";
				state.formBuilderDialog.isOpen = false;
				state.formBuilderDialog.mode = "create";
				state.formBuilderDialog.formId = null;
			})
			.addCase(createEvaluationForm.rejected, (state, action: any) => {
				if (action.payload?.status === 429) {
					state.createEvaluationForm.status = "error";
				} else {
					state.createEvaluationForm.status = "rejected";
					state.createEvaluationForm.errorMessage =
						action.payload ?? action.error?.message ?? i18next.t("evaluation-setup_err-creating-evaluation-form");
				}
			})
			// Update Evaluation Form
			.addCase(updateEvaluationForm.pending, (state) => {
				state.updateEvaluationForm.status = "pending";
				state.updateEvaluationForm.errorMessage = "";
			})
			.addCase(updateEvaluationForm.fulfilled, (state) => {
				state.updateEvaluationForm.status = "fulfilled";
				state.formBuilderDialog.isOpen = false;
				state.formBuilderDialog.mode = "create";
				state.formBuilderDialog.formId = null;
			})
			.addCase(updateEvaluationForm.rejected, (state, action: any) => {
				if (action.payload?.status === 429) {
					state.updateEvaluationForm.status = "error";
				} else {
					state.updateEvaluationForm.status = "rejected";
					state.updateEvaluationForm.errorMessage =
						action.payload ?? action.error?.message ?? i18next.t("evaluation-setup_err-updating-evaluation-form");
				}
			})
			// Delete Evaluation Form
			.addCase(deleteEvaluationForm.pending, (state) => {
				state.deleteEvaluationForm.status = "pending";
				state.deleteEvaluationForm.errorMessage = "";
			})
			.addCase(deleteEvaluationForm.fulfilled, (state, action) => {
				state.deleteEvaluationForm.status = "fulfilled";
				const deletedId = action.payload.formId;
				// Remove from templates list
				state.templates.data = state.templates.data.filter(
					(template) => template.id !== deletedId
				);
			})
			.addCase(deleteEvaluationForm.rejected, (state, action: any) => {
				if (action.payload?.status === 429) {
					state.deleteEvaluationForm.status = "error";
				} else {
					state.deleteEvaluationForm.status = "rejected";
					state.deleteEvaluationForm.errorMessage =
						action.payload ?? action.error?.message ?? i18next.t("evaluation-setup_err-deleting-evaluation-form");
				}
			})
			// 2-step form creation: Step 1 - Create form
			.addCase(createEvaluationFormStep1.pending, (state) => {
				state.formCreationProgress.step = "creating_form";
				state.formCreationProgress.errorMessage = "";
			})
			.addCase(createEvaluationFormStep1.fulfilled, (state, action) => {
				state.formCreationProgress.step = "adding_questions";
				state.formCreationProgress.formId = action.payload.id;
			})
			.addCase(createEvaluationFormStep1.rejected, (state, action: any) => {
				state.formCreationProgress.step = "failed";
				state.formCreationProgress.errorMessage =
					action.payload ?? action.error?.message ?? i18next.t("evaluation-setup_err-creating-form");
			})
			// 2-step form creation: Step 2 - Add questions
			.addCase(addQuestionsToForm.pending, (state) => {
				state.formCreationProgress.step = "adding_questions";
			})
			.addCase(addQuestionsToForm.fulfilled, (state) => {
				state.formCreationProgress.step = "completed";
				state.formBuilderDialog.isOpen = false;
			})
			.addCase(addQuestionsToForm.rejected, (state, action: any) => {
				state.formCreationProgress.step = "failed";
				state.formCreationProgress.errorMessage =
					action.payload ?? action.error?.message ?? i18next.t("evaluation-setup_err-adding-questions");
			})
			// Auto-save: Create form draft
			.addCase(createFormDraft.pending, (state) => {
				state.autoSaveStatus.status = "saving";
				state.autoSaveStatus.errorMessage = "";
			})
			.addCase(createFormDraft.fulfilled, (state, action) => {
				state.currentDraftFormId = action.payload.id;
				state.autoSaveStatus.status = "saved";
				state.autoSaveStatus.lastSaved = new Date().toISOString();
				state.hasUnsavedChanges = true;
			})
			.addCase(createFormDraft.rejected, (state, action: any) => {
				state.autoSaveStatus.status = "error";
				state.autoSaveStatus.errorMessage =
					action.payload ?? action.error?.message ?? i18next.t("evaluation-setup_err-creating-draft");
			})
			// Auto-save: Save questions
			.addCase(autoSaveQuestions.pending, (state) => {
				state.autoSaveStatus.status = "saving";
				state.autoSaveStatus.errorMessage = "";
			})
			.addCase(autoSaveQuestions.fulfilled, (state, action) => {
				state.autoSaveStatus.status = "saved";
				state.autoSaveStatus.lastSaved = new Date().toISOString();
				state.hasUnsavedChanges = true;
				// Store question ID mappings for local state update
				state.autoSaveStatus.lastSaveResults = action.payload;
			})
			.addCase(autoSaveQuestions.rejected, (state, action: any) => {
				state.autoSaveStatus.status = "error";
				state.autoSaveStatus.errorMessage =
					action.payload ?? action.error?.message ?? i18next.t("evaluation-setup_err-saving-questions");
			})
			// Auto-save: Update form header
			.addCase(updateFormHeader.pending, (state) => {
				state.autoSaveStatus.status = "saving";
				state.autoSaveStatus.errorMessage = "";
			})
			.addCase(updateFormHeader.fulfilled, (state) => {
				state.autoSaveStatus.status = "saved";
				state.autoSaveStatus.lastSaved = new Date().toISOString();
			})
			.addCase(updateFormHeader.rejected, (state, action: any) => {
				state.autoSaveStatus.status = "error";
				state.autoSaveStatus.errorMessage =
					action.payload ?? action.error?.message ?? i18next.t("evaluation-setup_err-updating-form-header");
			})
			// Change form status
			.addCase(changeFormStatus.pending, (state) => {
				state.autoSaveStatus.status = "saving";
				state.autoSaveStatus.errorMessage = "";
			})
			.addCase(changeFormStatus.fulfilled, (state) => {
				state.autoSaveStatus.status = "saved";
				state.autoSaveStatus.lastSaved = new Date().toISOString();
				state.hasUnsavedChanges = false;
			})
			.addCase(changeFormStatus.rejected, (state, action: any) => {
				state.autoSaveStatus.status = "error";
				state.autoSaveStatus.errorMessage =
					action.payload ?? action.error?.message ?? i18next.t("evaluation-setup_err-changing-status");
			})
			// Acknowledgement metadata
			.addCase(fetchAcknowledgementMetadata.pending, (state) => {
				state.acknowledgementMetadata.status = "pending";
			})
			.addCase(fetchAcknowledgementMetadata.fulfilled, (state, action) => {
				state.acknowledgementMetadata.status = "fulfilled";
				state.acknowledgementMetadata.data = action.payload;
			})
			.addCase(fetchAcknowledgementMetadata.rejected, (state) => {
				state.acknowledgementMetadata.status = "rejected";
			})
			// Evaluation program roles
			.addCase(fetchEvaluationProgramRoles.pending, (state) => {
				state.evaluationProgramRoles.status = "pending";
			})
			.addCase(fetchEvaluationProgramRoles.fulfilled, (state, action) => {
				state.evaluationProgramRoles.status = "fulfilled";
				state.evaluationProgramRoles.data = action.payload;
			})
			.addCase(fetchEvaluationProgramRoles.rejected, (state) => {
				state.evaluationProgramRoles.status = "rejected";
			})
			// Acknowledgement templates list
			.addCase(fetchAcknowledgementTemplates.pending, (state) => {
				state.acknowledgementTemplates.status = "pending";
			})
			.addCase(fetchAcknowledgementTemplates.fulfilled, (state, action) => {
				state.acknowledgementTemplates.status = "fulfilled";
				state.acknowledgementTemplates.data = action.payload;
			})
			.addCase(fetchAcknowledgementTemplates.rejected, (state, action: any) => {
				state.acknowledgementTemplates.status = "rejected";
				state.acknowledgementTemplates.error =
					action.payload ?? action.error?.message ?? i18next.t("common_error-occurred");
			})
			// Acknowledgement template detail
			.addCase(fetchAcknowledgementTemplateDetail.pending, (state) => {
				state.acknowledgementDialog.detailStatus = "pending";
			})
			.addCase(fetchAcknowledgementTemplateDetail.fulfilled, (state, action) => {
				state.acknowledgementDialog.detailStatus = "fulfilled";
				state.acknowledgementDialog.detail = action.payload;
			})
			.addCase(fetchAcknowledgementTemplateDetail.rejected, (state) => {
				state.acknowledgementDialog.detailStatus = "rejected";
			})
			// Create acknowledgement template
			.addCase(createAcknowledgementTemplate.pending, (state) => {
				state.acknowledgementDialog.saveStatus = "pending";
				state.acknowledgementDialog.saveError = "";
			})
			.addCase(createAcknowledgementTemplate.fulfilled, (state) => {
				state.acknowledgementDialog.saveStatus = "fulfilled";
				state.acknowledgementDialog.isOpen = false;
				state.acknowledgementDialog.template = null;
				state.acknowledgementDialog.detail = null;
				state.acknowledgementDialog.detailStatus = "idle";
				state.acknowledgementDialog.saveError = "";
			})
			.addCase(createAcknowledgementTemplate.rejected, (state, action: any) => {
				state.acknowledgementDialog.saveStatus = "error";
				state.acknowledgementDialog.saveError =
					action.payload ?? action.error?.message ?? i18next.t("common_error-occurred");
			})
			// Update acknowledgement template
			.addCase(updateAcknowledgementTemplate.pending, (state) => {
				state.acknowledgementDialog.saveStatus = "pending";
				state.acknowledgementDialog.saveError = "";
			})
			.addCase(updateAcknowledgementTemplate.fulfilled, (state) => {
				state.acknowledgementDialog.saveStatus = "fulfilled";
				state.acknowledgementDialog.isOpen = false;
				state.acknowledgementDialog.template = null;
				state.acknowledgementDialog.detail = null;
				state.acknowledgementDialog.detailStatus = "idle";
				state.acknowledgementDialog.saveError = "";
			})
			.addCase(updateAcknowledgementTemplate.rejected, (state, action: any) => {
				state.acknowledgementDialog.saveStatus = "error";
				state.acknowledgementDialog.saveError =
					action.payload ?? action.error?.message ?? i18next.t("common_error-occurred");
			})
			// Delete acknowledgement template
			.addCase(deleteAcknowledgementTemplateThunk.pending, (state) => {
				state.deleteAcknowledgementTemplate.status = "pending";
			})
			.addCase(deleteAcknowledgementTemplateThunk.fulfilled, (state) => {
				state.deleteAcknowledgementTemplate.status = "fulfilled";
			})
			.addCase(deleteAcknowledgementTemplateThunk.rejected, (state, action: any) => {
				state.deleteAcknowledgementTemplate.status = "error";
				state.deleteAcknowledgementTemplate.errorMessage =
					action.payload ?? action.error?.message ?? i18next.t("common_error-occurred");
			})
			// Evaluation programs list
			.addCase(fetchEvaluationPrograms.pending, (state) => {
				state.evaluationPrograms.status = "pending";
			})
			.addCase(fetchEvaluationPrograms.fulfilled, (state, action) => {
				state.evaluationPrograms.status = "fulfilled";
				state.evaluationPrograms.data = action.payload;
			})
			.addCase(fetchEvaluationPrograms.rejected, (state, action: any) => {
				state.evaluationPrograms.status = "rejected";
				state.evaluationPrograms.error =
					action.payload ?? action.error?.message ?? i18next.t("common_error-occurred");
			})
			// Evaluation program detail
			.addCase(fetchEvaluationProgramDetail.pending, (state) => {
				state.programDetailDialog.detailStatus = "pending";
			})
			.addCase(fetchEvaluationProgramDetail.fulfilled, (state, action) => {
				state.programDetailDialog.detailStatus = "fulfilled";
				state.programDetailDialog.detail = action.payload;
			})
			.addCase(fetchEvaluationProgramDetail.rejected, (state) => {
				state.programDetailDialog.detailStatus = "rejected";
			})
			// Fetch locations
			.addCase(fetchEvaluationFormLocations.pending, (state) => {
				state.fetchLocations.status = "pending";
				state.fetchLocations.error = "";
			})
			.addCase(fetchEvaluationFormLocations.fulfilled, (state, action) => {
				state.fetchLocations.status = "fulfilled";
				state.fetchLocations.data = action.payload;
			})
			.addCase(fetchEvaluationFormLocations.rejected, (state, action: any) => {
				state.fetchLocations.status = "rejected";
				state.fetchLocations.error =
					action.payload ?? action.error?.message ?? i18next.t("evaluation-setup_err-fetching-locations");
			})
			// Manage locations
			.addCase(manageEvaluationFormLocations.pending, (state) => {
				state.manageLocations.status = "pending";
				state.manageLocations.errorMessage = "";
			})
			.addCase(manageEvaluationFormLocations.fulfilled, (state) => {
				state.manageLocations.status = "fulfilled";
				state.manageLocationsDialog.isOpen = false;
				state.manageLocationsDialog.template = null;
			})
			.addCase(manageEvaluationFormLocations.rejected, (state, action: any) => {
				state.manageLocations.status = "rejected";
				state.manageLocations.errorMessage =
					action.payload ?? action.error?.message ?? i18next.t("evaluation-setup_err-managing-locations");
			})
			// Evaluation Program Metadata
			.addCase(fetchEvaluationProgramMetadata.pending, (state) => {
				state.evaluationProgramMetadata.status = "pending";
			})
			.addCase(fetchEvaluationProgramMetadata.fulfilled, (state, action) => {
				state.evaluationProgramMetadata.status = "fulfilled";
				state.evaluationProgramMetadata.data = action.payload;
			})
			.addCase(fetchEvaluationProgramMetadata.rejected, (state) => {
				state.evaluationProgramMetadata.status = "rejected";
			})
			// Create Evaluation Program
			.addCase(createEvaluationProgram.pending, (state) => {
				state.createEvaluationProgram.status = "pending";
				state.createEvaluationProgram.errorMessage = "";
			})
			.addCase(createEvaluationProgram.fulfilled, (state) => {
				state.createEvaluationProgram.status = "fulfilled";
				state.programBuilderDialog.isOpen = false;
			})
			.addCase(createEvaluationProgram.rejected, (state, action: any) => {
				state.createEvaluationProgram.status = "rejected";
				state.createEvaluationProgram.errorMessage =
					action.payload ?? action.error?.message ?? i18next.t("evaluation-setup_err-creating-program");
			})
			// Update Evaluation Program
			.addCase(updateEvaluationProgram.pending, (state) => {
				state.updateEvaluationProgram.status = "pending";
				state.updateEvaluationProgram.errorMessage = "";
			})
			.addCase(updateEvaluationProgram.fulfilled, (state) => {
				state.updateEvaluationProgram.status = "fulfilled";
				state.programBuilderDialog.isOpen = false;
			})
			.addCase(updateEvaluationProgram.rejected, (state, action: any) => {
				state.updateEvaluationProgram.status = "rejected";
				state.updateEvaluationProgram.errorMessage =
					action.payload ?? action.error?.message ?? i18next.t("evaluation-setup_err-updating-program");
			})
			// Delete Evaluation Program
			.addCase(deleteEvaluationProgramThunk.pending, (state) => {
				state.deleteEvaluationProgram.status = "pending";
				state.deleteEvaluationProgram.errorMessage = "";
			})
			.addCase(deleteEvaluationProgramThunk.fulfilled, (state, action) => {
				state.deleteEvaluationProgram.status = "fulfilled";
				const deletedId = action.payload as number;
				state.evaluationPrograms.data = state.evaluationPrograms.data.filter(
					(p) => p.id !== deletedId
				);
			})
			.addCase(deleteEvaluationProgramThunk.rejected, (state, action: any) => {
				state.deleteEvaluationProgram.status = "rejected";
				state.deleteEvaluationProgram.errorMessage =
					action.payload ?? action.error?.message ?? i18next.t("evaluation-setup_err-deleting-program");
			})
			// Change Evaluation Program Status
			.addCase(changeEvaluationProgramStatusThunk.pending, (state) => {
				state.changeEvaluationProgramStatus.status = "pending";
				state.changeEvaluationProgramStatus.errorMessage = "";
			})
			.addCase(changeEvaluationProgramStatusThunk.fulfilled, (state) => {
				state.changeEvaluationProgramStatus.status = "fulfilled";
			})
			.addCase(changeEvaluationProgramStatusThunk.rejected, (state, action: any) => {
				state.changeEvaluationProgramStatus.status = "rejected";
				state.changeEvaluationProgramStatus.errorMessage =
					action.payload ?? action.error?.message ?? i18next.t("evaluation-setup_err-changing-program-status");
			})
			// Program Skills
			.addCase(fetchProgramSkills.pending, (state) => {
				state.programSkills.status = "pending";
			})
			.addCase(fetchProgramSkills.fulfilled, (state, action) => {
				state.programSkills.status = "fulfilled";
				state.programSkills.data = action.payload;
			})
			.addCase(fetchProgramSkills.rejected, (state) => {
				state.programSkills.status = "rejected";
			})
			// Program Jobs
			.addCase(fetchProgramJobs.pending, (state) => {
				state.programJobs.status = "pending";
			})
			.addCase(fetchProgramJobs.fulfilled, (state, action) => {
				state.programJobs.status = "fulfilled";
				state.programJobs.data = action.payload;
			})
			.addCase(fetchProgramJobs.rejected, (state) => {
				state.programJobs.status = "rejected";
			})
			// Skills Matrix Filters
			.addCase(fetchSkillsMatrixFilters.pending, (state) => {
				state.skillsMatrixFilters.status = "pending";
			})
			.addCase(fetchSkillsMatrixFilters.fulfilled, (state, action: any) => {
				const { locations, jobs, skills } = action.payload;
				state.skillsMatrixFilters.status = "fulfilled";
				state.skillsMatrixFilters.data = {
					locations: (locations ?? []).slice().sort((a: any, b: any) =>
						a.name?.trim().localeCompare(b.name?.trim())
					),
					jobs: (jobs ?? []).slice().sort((a: any, b: any) =>
						a.name?.trim().localeCompare(b.name?.trim())
					),
					skills: (skills ?? []).slice().sort((a: any, b: any) =>
						a.name?.trim().localeCompare(b.name?.trim())
					),
				};
			})
			.addCase(fetchSkillsMatrixFilters.rejected, (state) => {
				state.skillsMatrixFilters.status = "rejected";
			})
			// Question Categories
			.addCase(fetchCategories.pending, (state) => {
				state.categories.status = "pending";
				state.categories.error = "";
			})
			.addCase(fetchCategories.fulfilled, (state, action) => {
				state.categories.status = "fulfilled";
				state.categories.data = action.payload;
			})
			.addCase(fetchCategories.rejected, (state, action: any) => {
				state.categories.status = "rejected";
				state.categories.error =
					action.payload ?? action.error?.message ?? i18next.t("evaluation-setup_err-fetching-categories");
			})
			.addCase(createQuestionCategory.pending, (state) => {
				state.createCategory.status = "pending";
				state.createCategory.errorMessage = "";
			})
			.addCase(createQuestionCategory.fulfilled, (state) => {
				state.createCategory.status = "fulfilled";
				state.createCategory.errorMessage = "";
			})
			.addCase(createQuestionCategory.rejected, (state, action: any) => {
				state.createCategory.status = "error";
				state.createCategory.errorMessage =
					action.payload ?? action.error?.message ?? i18next.t("evaluation-setup_err-creating-category");
			})
			.addCase(updateQuestionCategory.pending, (state) => {
				state.updateCategory.status = "pending";
				state.updateCategory.errorMessage = "";
			})
			.addCase(updateQuestionCategory.fulfilled, (state) => {
				state.updateCategory.status = "fulfilled";
				state.updateCategory.errorMessage = "";
			})
			.addCase(updateQuestionCategory.rejected, (state, action: any) => {
				state.updateCategory.status = "error";
				state.updateCategory.errorMessage =
					action.payload ?? action.error?.message ?? i18next.t("evaluation-setup_err-updating-category");
			})
			.addCase(deleteQuestionCategory.pending, (state) => {
				state.deleteCategory.status = "pending";
				state.deleteCategory.errorMessage = "";
			})
			.addCase(deleteQuestionCategory.fulfilled, (state) => {
				state.deleteCategory.status = "fulfilled";
				state.deleteCategory.errorMessage = "";
			})
			.addCase(deleteQuestionCategory.rejected, (state, action: any) => {
				state.deleteCategory.status = "error";
				state.deleteCategory.errorMessage =
					action.payload ?? action.error?.message ?? i18next.t("evaluation-setup_err-deleting-category");
			})
			// Scoring metadata
			.addCase(fetchScoringMetadata.pending, (state) => {
				state.scoringMetadata.status = "pending";
			})
			.addCase(fetchScoringMetadata.fulfilled, (state, action: any) => {
				state.scoringMetadata.status = "fulfilled";
				state.scoringMetadata.data = action.payload;
			})
			.addCase(fetchScoringMetadata.rejected, (state) => {
				state.scoringMetadata.status = "rejected";
			})
			// Scoring config
			.addCase(fetchScoringConfig.pending, (state) => {
				state.scoringDialog.detailStatus = "pending";
			})
			.addCase(fetchScoringConfig.fulfilled, (state, action: any) => {
				state.scoringDialog.detailStatus = "fulfilled";
				state.scoringDialog.detail = action.payload;
			})
			.addCase(fetchScoringConfig.rejected, (state) => {
				state.scoringDialog.detailStatus = "rejected";
				state.scoringDialog.detail = null;
			})
			// Save scoring config
			.addCase(saveScoringConfig.pending, (state) => {
				state.scoringDialog.saveStatus = "pending";
				state.scoringDialog.saveError = "";
			})
			.addCase(saveScoringConfig.fulfilled, (state) => {
				state.scoringDialog.saveStatus = "fulfilled";
				state.scoringDialog.saveError = "";
			})
			.addCase(saveScoringConfig.rejected, (state, action: any) => {
				state.scoringDialog.saveStatus = "rejected";
				state.scoringDialog.saveError =
					typeof action.payload === "string"
						? action.payload
						: action.payload?.message ??
							action.error?.message ??
							i18next.t("evaluation-setup_err-saving-scoring-config");
			});
	},
});

export const {
	resetEditTemplate,
	resetTemplateDetails,
	// Form Builder Dialog
	openFormBuilderDialog,
	closeFormBuilderDialog,
	resetCreateEvaluationForm,
	resetUpdateEvaluationForm,
	resetDeleteEvaluationForm,
	resetFormCreationProgress,
	// Auto-save actions
	setCurrentDraftFormId,
	setHasUnsavedChanges,
	resetAutoSaveStatus,
	clearAutoSaveResults,
	// Acknowledgement actions
	openCreateAcknowledgementDialog,
	openEditAcknowledgementDialog,
	openViewAcknowledgementDialog,
	closeAcknowledgementDialog,
	resetDeleteAcknowledgementTemplate,
	// Evaluation Program actions
	openProgramDetailDialog,
	closeProgramDetailDialog,
	openProgramBuilderDialog,
	closeProgramBuilderDialog,
	resetChangeEvaluationProgramStatus,
	resetDeleteEvaluationProgram,
	// Manage Locations actions
	openManageLocationsDialog,
	closeManageLocationsDialog,
	// Scoring dialog actions
	openScoringDialog,
	closeScoringDialog,
	resetSaveScoringStatus,
	// Question Category actions
	resetCreateCategory,
	resetUpdateCategory,
	resetDeleteCategory,
} = SkillEvaluation.actions;

export default SkillEvaluation.reducer;
